import React from 'react';
import { RouteComponentProps } from 'react-router-dom';

import { useMutation } from '@apollo/react-hooks';
import { Form, Formik } from 'formik';
import _get from 'lodash/get';
import useReactRouter from 'use-react-router';
import * as Yup from 'yup';
import useDocumentTitle from '../hooks/use-document-title';
import { handleFormError } from '../utils/gql-utils';

import SpinnerButton from '@do/walrus/SpinnerButton';
import Text from '@do/walrus/Text';
import TextInput from '@do/walrus/TextInput';
import { FORGOT_PASSWORD } from '../queries/queries';
import { ErrorBanner, FormWrap, PaddedContents, Wrap } from './Common';
import Header from './Header';

import defaultBg from '../assets/backgrounds/Login_Default.jpg';
import copy from '../copy';
import Background from './Background';

const passwordForgotValidation = Yup.object().shape({
  email: Yup.string()
    .email(copy.form.validation.email_valid)
    .required(copy.form.validation.email_required)
});

interface ForgotPasswordGqlResponse {
  forgotPassword: {
    status: string;
  };
}

interface ForgotPasswordGqlVariables {
  forgotPasswordRequest: {
    email: string;
  };
}

const PasswordForgot = ({ location }: RouteComponentProps) => {
  const [genericServerError, setGenericServerError] = React.useState(null);
  const [forgotPassword, { loading }] = useMutation<
    ForgotPasswordGqlResponse,
    ForgotPasswordGqlVariables
  >(FORGOT_PASSWORD);
  const { history } = useReactRouter();

  useDocumentTitle(copy.form.password_forgot_title);

  return (
    <>
      <Header isLogin={true} />
      <Wrap>
        <Background url={defaultBg} />
        <FormWrap>
          <PaddedContents>
            <h4>{copy.form.password_forgot.title}</h4>
            <Text>{copy.form.password_forgot.details}</Text>
            <Formik
              initialValues={{
                email: _get(location, 'state.email') || ''
              }}
              validationSchema={passwordForgotValidation}
              onSubmit={(values, { setErrors }) => {
                setGenericServerError(null); /* Reset displayed error */
                forgotPassword({
                  variables: {
                    forgotPasswordRequest: values
                  }
                })
                  .then(() => {
                    history.push({
                      pathname: '/login',
                      state: { success: 'forgot' }
                    });
                  })
                  .catch(handleFormError(setErrors, setGenericServerError));
              }}
            >
              {({ errors, values, touched }) => (
                <Form>
                  {genericServerError && (
                    <ErrorBanner error={genericServerError} />
                  )}

                  <TextInput
                    type="email"
                    placeholder={copy.form.password_forgot.placeholder}
                    name="email"
                    required
                    label={copy.form.password_forgot.label}
                    touched={touched.email}
                    error={errors.email}
                    value={values.email}
                    autoFocus
                  />
                  <SpinnerButton
                    className="do-u-mt--medium"
                    type="submit"
                    fullWidth
                    spinning={loading}
                    disabled={loading}
                    loadingMessage={copy.form.password_forgot.loading}
                  >
                    {copy.form.password_forgot.button_text}
                  </SpinnerButton>
                </Form>
              )}
            </Formik>
          </PaddedContents>
        </FormWrap>
      </Wrap>
    </>
  );
};

export default PasswordForgot;
